import { createContext, useContext, useEffect, useRef, useState } from 'react'
import type {
  Provenance,
  ProvenanceSource,
  ProvenanceStep,
  SheetSource,
} from '@rollcall/contracts'
import { ABILITY_KEYS, ABILITY_NAMES } from '@rollcall/rules'
import type { TFunction } from 'i18next'
import { useT } from './i18n-context.js'
import { contentName, traitLabel, valueLabel } from './labels.js'

/**
 * Разбор величины по тапу — то, ради чего писался провенанс (D5).
 *
 * Правило простое: число на листе показывается только через
 * `ValueButton`, а он всегда открывает разбор. Величины без разбора
 * на листе нет, и это проверяется тестом, а не вниманием.
 *
 * Разбор — шторка снизу. На телефоне это единственная раскладка,
 * которая не накрывает само число: при открытии оно подводится
 * к верху экрана, а шторка занимает низ.
 */

export type ProvenanceView = {
  readonly values: Readonly<Record<string, Provenance>>
  readonly sources: Readonly<Record<string, SheetSource>>
}

type Opened = { readonly path: string; readonly label: string }

type ProvenanceState = ProvenanceView & {
  readonly open: (opened: Opened) => void
}

const ProvenanceContext = createContext<ProvenanceState | null>(null)

/** Ключ характеристики (`dex`) → полное имя (`dexterity`) для подписи. */
const ABILITY_BY_KEY = Object.fromEntries(
  ABILITY_NAMES.map((name) => [ABILITY_KEYS[name], name]),
) as Readonly<Record<string, string>>

/**
 * Значение величины: число либо набор признаков (D21.3).
 *
 * Признаки движок хранит ключами, и на экран они попадать не должны:
 * набор переводится поимённо, а путь величины говорит, какой это
 * набор — от него зависит разбор ключа.
 */
export function formatValue(
  value: Provenance['value'],
  options?: { readonly t: TFunction; readonly path: string },
): string {
  if (Array.isArray(value)) {
    if (value.length === 0) return '—'
    return options === undefined
      ? value.join(', ')
      : value
          .map((item) => traitLabel(options.t, options.path, String(item)))
          .join(', ')
  }
  return signedNumber(value)
}

/**
 * Число со знаком: знак стоит всегда, включая ноль, — «0» и «+0»
 * в столбце модификаторов читаются по-разному, и первое выглядит
 * незаполненным полем.
 *
 * Минус — типографский (U+2212), а не дефис: в столбце цифр дефис
 * заметно короче и сбивает выравнивание.
 */
export function signedNumber(value: number): string {
  if (value > 0) return `+${value}`
  if (value < 0) return `\u2212${Math.abs(value)}`
  return '+0'
}

/** Число как число, без знака: КД и хиты со знаком «плюс» читаются странно. */
export function formatPlain(
  value: Provenance['value'],
  options?: { readonly t: TFunction; readonly path: string },
): string {
  return Array.isArray(value) ? formatValue(value, options) : String(value)
}

/**
 * Источник шага по-человечески.
 *
 * Запись контента называется своим именем из источника — в разборе
 * не должно быть `srd:chain_mail`. Правила системы и характеристики
 * переводятся, потому что это наши ключи, а не контент.
 */
export function describeSource(
  source: ProvenanceSource,
  sources: Readonly<Record<string, SheetSource>>,
  t: TFunction,
): string {
  if (source.kind === 'content') {
    return contentName(t, source.id, sources[source.id]?.name)
  }
  if (source.kind === 'ability') {
    const ability = ABILITY_BY_KEY[source.key.split('.').pop() ?? '']
    return ability === undefined ? source.key : t(`abilities.${ability}`)
  }
  if (source.kind === 'journal') {
    return t('provenance.journal')
  }
  // Ключи правил известны наперёд: их задаёт движок, а не контент.
  const key = source.key.replace(/^rules\./, '')
  return t(`provenance.ruleset.${key}`, { defaultValue: t('provenance.rulesetOther') })
}

/**
 * Что шаг сделал с числом: «основа 10», «+2», «×2», «не выше 20».
 *
 * У `grant` значение — ключ признака, и путь величины нужен, чтобы
 * его разобрать: один и тот же `poison` в сопротивлениях и в языках
 * означал бы разное.
 */
export function describeStep(step: ProvenanceStep, t: TFunction, path = ''): string {
  switch (step.op) {
    case 'set':
      return t('provenance.op.set', { value: step.value })
    case 'add':
      return t('provenance.op.add', { value: signedNumber(step.value) })
    case 'mul':
      return t('provenance.op.mul', { value: step.value })
    case 'add_capped':
      return t('provenance.op.addCapped', { value: step.value, max: step.max })
    case 'clamp':
      return t('provenance.op.clamp', {
        min: step.value.min ?? '—',
        max: step.value.max ?? '—',
      })
    case 'grant':
      return t('provenance.op.grant', { value: traitLabel(t, path, step.value) })
  }
}

export function ProvenanceProvider({
  view,
  children,
}: {
  readonly view: ProvenanceView
  readonly children: React.ReactNode
}) {
  const t = useT()
  const [opened, setOpened] = useState<Opened | null>(null)

  return (
    <ProvenanceContext.Provider value={{ ...view, open: setOpened }}>
      {children}
      {opened !== null && (
        <ProvenanceDialog
          path={opened.path}
          label={opened.label}
          view={view}
          t={t}
          onClose={() => {
            setOpened(null)
          }}
        />
      )}
    </ProvenanceContext.Provider>
  )
}

function useProvenance(): ProvenanceState {
  const state = useContext(ProvenanceContext)
  if (state === null) throw new Error('ProvenanceProvider не найден')
  return state
}

/**
 * Число на листе. Не «span с числом», а кнопка: то, что открывается
 * тапом, обязано открываться и с клавиатуры, и голосовым доступом.
 */
export function ValueButton({
  path,
  label,
  signed = false,
  display,
  note,
  proficient,
  onRoll,
  rollLabel,
  rollBusy = false,
}: {
  readonly path: string
  readonly label: string
  /** Модификаторы показываются со знаком, КД и хиты — без. */
  readonly signed?: boolean
  /**
   * Крупное число, если оно не совпадает с величиной разбора.
   * Нужно характеристикам: крупно модификатор, потому что им
   * пользуются в каждом броске, а разбирается значение, из которого
   * он выведен, — в шторке это и видно.
   */
  readonly display?: string | undefined
  readonly note?: string | undefined
  /**
   * Владение: спасбросок или навык, куда идёт бонус мастерства.
   * Отдельным признаком, а не по тексту приписки, — оформление
   * не должно разбирать строку, чтобы понять смысл (D28, п. 4).
   */
  readonly proficient?: boolean | undefined
  /**
   * Бросок этой величины (D12). Кнопка стоит рядом с числом,
   * а не вместо него: тап по числу открывает разбор, и подменять
   * одно другим значит отобрать разбор ради броска.
   */
  readonly onRoll?: (() => void) | undefined
  readonly rollLabel?: string | undefined
  readonly rollBusy?: boolean | undefined
}) {
  const t = useT()
  const { values, open } = useProvenance()
  const provenance = values[path]

  if (provenance === undefined) return null

  const shown =
    display ??
    (signed
      ? formatValue(provenance.value, { t, path })
      : formatPlain(provenance.value, { t, path }))

  const button = (
    <button
      type="button"
      className="app-value-button"
      data-path={path}
      {...(proficient === true ? { 'data-proficient': '' } : {})}
      aria-label={t('provenance.explain', { label, value: shown })}
      onClick={(event) => {
        // Число подводится к верху: шторка занимает низ экрана,
        // и без этого на телефоне она закрыла бы само число.
        event.currentTarget.scrollIntoView?.({ block: 'start' })
        open({ path, label })
      }}
    >
      <span className="app-value-label">{label}</span>
      <span className="app-value-number">{shown}</span>
      {note !== undefined && <span className="app-value-note">{note}</span>}
    </button>
  )

  if (onRoll === undefined) return button

  return (
    <span className="app-value-row">
      {button}
      <button
        type="button"
        className="app-button app-button-quiet app-value-roll"
        disabled={rollBusy}
        aria-label={t('roll.rollValue', { label })}
        onClick={onRoll}
      >
        {rollLabel ?? t('roll.roll')}
      </button>
    </span>
  )
}

function ProvenanceDialog({
  path,
  label,
  view,
  t,
  onClose,
}: {
  readonly path: string
  readonly label: string
  readonly view: ProvenanceView
  readonly t: TFunction
  readonly onClose: () => void
}) {
  const dialog = useRef<HTMLDivElement>(null)
  const provenance = view.values[path]

  useEffect(() => {
    dialog.current?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  if (provenance === undefined) return null

  return (
    <>
      {/* Тап вне разбора закрывает его: на телефоне это привычнее кнопки. */}
      <div className="app-scrim" onClick={onClose} data-testid="provenance-scrim" />
      <div
        className="app-breakdown"
        role="dialog"
        aria-modal="true"
        aria-labelledby="breakdown-title"
        tabIndex={-1}
        ref={dialog}
      >
        <div className="app-breakdown-head">
          <h3 id="breakdown-title">{t('provenance.title')}</h3>
          <button
            type="button"
            className="app-button app-button-quiet"
            onClick={onClose}
          >
            {t('provenance.close')}
          </button>
        </div>

        <p className="app-breakdown-total">
          <span>{label}</span>
          <strong>{formatPlain(provenance.value, { t, path })}</strong>
        </p>

        <ol className="app-steps">
          {provenance.steps.map((step, index) => (
            <li key={`${index}-${step.op}`} className="app-step">
              <span className="app-step-source">
                {describeSource(step.source, view.sources, t)}
                {/* Из какой величины взято слагаемое. Без этого две
                    строки кожаного доспеха — «основа 11» и «+0» —
                    выглядят повтором, а вторая на самом деле прибавка
                    Ловкости, и ноль в ней объясняет, почему КД
                    одиннадцать, а не четырнадцать (D5). */}
                {'ref' in step && step.ref !== undefined && (
                  <span className="app-step-ref">
                    {' · '}
                    {valueLabel(t, step.ref) ?? step.ref}
                  </span>
                )}
              </span>
              <span className="app-step-op">{describeStep(step, t, path)}</span>
              <span className="app-step-result">
                {formatPlain(step.result, { t, path })}
              </span>
            </li>
          ))}
        </ol>
      </div>
    </>
  )
}
